import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { FastField, Field, Formik } from 'formik';
import _ from 'lodash';

import { VIEW_CTX } from '../constants';
import { URLS } from 'app/config/urls';

import notification from 'app/config/notification';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { errorHandler } from 'app/utils/errorHandler';

import { fetchLineTransfers } from 'app/store/slices/line';

import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';

import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import CustomFindField from '../components/FindField';
import Loading from 'app/layouts/Loading';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import DefaultTable from '../components/DefaultTable';
import SimpleAlert from '../components/SimpleAlert';
import RequestProcess from '../components/RequestProcess';
import InfoLineCompany from '../components/InfoLineCompany';
import FindPermissionary from '../pages/drawers/FindPermissionary';

import { transferInitialValues } from '../form-configs/transfer-initial-values';
import { transferColumns } from '../table-configs/columns-schemas';
import { transferValidationSchema } from '../form-configs/validation-schemas';
import { itemIsAreadySelected } from '../form-configs/form-helpers';
import { SERVICES_CODES } from '../../../config/services';
import { validateFormikForm } from '../../../utils/forms/functions';
import helpers from '../../../utils/helpers';
import companyEndpoints from '../../../api/services/companyEndpoints';
import { generatePath } from 'react-router-dom';

const PERMISSIONARY_NOT_FOUND = 'Permissionário não existe no cadastro da agência, favor verificar';
const REQUIRED_FIELD = 'Campo obrigatório';

const TransferSlic = (props) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { cdLinha, loading, setLoading } = props;

    const screenIsMounted = React.useRef();

    const servicesFilter = useRef([{ cdServico: SERVICES_CODES.SLIC_TRANSFERENCIA_PERMISSAO }]).current;

    const [loadingTable, setLoadingTable] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [formInitials, setFormInitials] = useState(transferInitialValues);
    const [openAlert, setOpenAlert] = useState(false);
    const [atualPermNameFieldError, setAtualPermNameFieldError] = useState(null);
    const [substPermNameFieldError, setSubstPermNameFieldError] = useState(null);
    const [permDocumentFieldError, setPermDocumentFieldError] = useState(null);
    const [requestProcessValues, setRequestProcessValues] = useState({});
    const [selectedViewContext, setSelectedViewContext] = useState(null);
    const [selectedActualPermissionary, setSelectedActualPermissionary] = useState({});
    const [selectedSubstitutePermissionary, setSelectedSubstitutePermissionary] = useState({});
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [rows, setRows] = useState([]);

    const [docAtual, setDocAtual] = useState('');
    const [docSucessor, setDocSucessor] = useState('');

    const lineTransfers = useSelector((state) => state.line.lineTransfers);
    const selectedLine = useSelector((state) => state.line.selected);

    const prepareRows = (data) => {
        if (!data) return [];
        return data.map((row) => {
            return {
                nuProcesso: row.nuProcesso,
                nuTermoTransferencia: row.nuTermoTransferencia,
                dtTransferencia: row.dtTransferencia,
                permissionarioAnterior: row.deNomePermissionarioAntigo,
                permissionarioAtual: row.deNomePermissionarioNovo,
            };
        });
    };

    const prepareCompany = (company) => {
        if (company && Object.keys(company).length > 0) {
            return {
                boAtivo: company.boSituacaoOrgao,
                cdEmpresa: company.cdEmpresa,
                deCpfCnpj: company.cdTipoPessoaFisica
                    ? company.deCpf || company.deCpfCnpj || ''
                    : company.deCnpjMatriz || '',
                deNomePermissionario: company.cdTipoPessoaFisica
                    ? company.nmCompleto || ''
                    : company.deRazaoSocial || '',
                permissionario: false,
                tpCategoriaCnh: 'A',
            };
        }
    };

    useEffect(() => {
        (async () => {
            setLoadingTable(true);
            const promisses = [];
            const transfParams = { page: page, size: pageSize, request: { cdLinha: cdLinha } };
            promisses.push(dispatch(fetchLineTransfers(transfParams)));
            if (promisses.length > 0) await Promise.all(promisses);
            if (screenIsMounted.current) setLoadingTable(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    useEffect(() => {
        screenIsMounted.current = true;
        (async () => {
            const transfParams = { page: page, size: pageSize, request: { cdLinha: cdLinha } };
            setLoading(true);
            const promisses = [];
            if (lineTransfers.length === 0) promisses.push(dispatch(fetchLineTransfers(transfParams)));
            if (promisses.length > 0) await Promise.all(promisses);
            if (screenIsMounted.current) setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        (() => {
            setRows(prepareRows(lineTransfers.content));
        })();
    }, [lineTransfers]);

    const handleChangePageSize = (e) => {
        setPage(0);
        setPageSize(parseInt(e.target.value));
    };

    const handleChangePage = (e, page) => {
        setPage(page);
    };

    const openDrawerWithContext = (context) => {
        setSelectedViewContext(context);
        setOpenDrawer(true);
    };

    const handleCloseDrawer = () => {
        setOpenDrawer(false);
    };

    const handleCancelTransfer = () => {
        history.push(generatePath(URLS.LINES_MANAGE));
    };

    const reset = () => {
        setFormInitials(transferInitialValues);
        setSelectedActualPermissionary({});
        setSelectedSubstitutePermissionary({});
        setRequestProcessValues({});
        setPage(0);
    };

    const getPermissionaryDataByDocument = async (document, substitute) => {
        let invalidDocument = document === '' || !helpers.validateCpfCnpj(document);
        if (invalidDocument) {
            if (document === '') {
                return setPermDocumentFieldError(REQUIRED_FIELD);
            }
            return setPermDocumentFieldError('Verifique o número digitado');
        }
        try {
            let response;
            if (!substitute) {
                let filters = {
                    cdLinha: Number(cdLinha),
                    campoOrdenacao: 'linhaPermissionario.cdLinhaPermissionario',
                    direcaoOrdenacao: 'desc',
                    deCpfCnpj: document,
                };
                const params = { page: 0, size: 1, request: filters };
                response = await lineEndpoint.getPermissionaries(params);
                if (response && response.status === 200 && response.data?.content) {
                    return response.data.content[0];
                }
            } else {
                const params = { page: 0, size: 1, request: { txtBusca: document } };
                response = await companyEndpoints.getCompanies(params);
                if (response && response.status === 200 && response.data?.content) {
                    return prepareCompany(response.data.content[0]);
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
    };

    const handleSelectPermissionaryByDocument = async (document, field) => {
        setLoading(true);
        if (field === 'docAnterior') {
            const response = await getPermissionaryDataByDocument(document);
            if (!response || response === '') {
                setSelectedActualPermissionary({});
                setAtualPermNameFieldError(PERMISSIONARY_NOT_FOUND);
            } else {
                setSelectedActualPermissionary(() => response);
                setAtualPermNameFieldError(null);
            }
        } else {
            const response = await getPermissionaryDataByDocument(document, true);
            if (!response || response === '') {
                setSelectedSubstitutePermissionary({});
                setSubstPermNameFieldError(PERMISSIONARY_NOT_FOUND);
            } else {
                let config = { selected: selectedActualPermissionary, toSelect: response };
                if (!itemIsAreadySelected(config)) {
                    setSelectedSubstitutePermissionary(() => response);
                    setSubstPermNameFieldError(null);
                    setPermDocumentFieldError(null);
                }
            }
        }
        setOpenAlert(false);
        setLoading(false);
    };

    const handleSelectPermissionary = (permissionaryData) => {
        let config;

        if (selectedViewContext === VIEW_CTX.searchActualPermissionary) {
            config = { selected: selectedSubstitutePermissionary, toSelect: permissionaryData };
            if (!itemIsAreadySelected(config)) {
                setSelectedActualPermissionary(() => permissionaryData);
                setDocAtual(permissionaryData.deCpfCnpj);
                setAtualPermNameFieldError(null);
            } else {
                setOpenAlert(true);
            }
        } else if (selectedViewContext === VIEW_CTX.searchSubstitutePermissionary) {
            config = { selected: selectedActualPermissionary, toSelect: permissionaryData };
            if (!itemIsAreadySelected(config)) {
                setSelectedSubstitutePermissionary(() => permissionaryData);
                setDocSucessor(
                    permissionaryData.deCnpjMatriz || permissionaryData.deCpfCnpj || permissionaryData.deCpf,
                );
                setPermDocumentFieldError(null);
                setSubstPermNameFieldError(null);
            } else {
                setOpenAlert(true);
            }
        }
    };

    const validateBeforeSave = (formikBag) => {
        setAtualPermNameFieldError(null);
        setSubstPermNameFieldError(null);
        setPermDocumentFieldError(null);
        if (!selectedActualPermissionary?.cdLinhaPermissionario) {
            setAtualPermNameFieldError(REQUIRED_FIELD);
        }
        if (!selectedSubstitutePermissionary?.cdEmpresa) {
            if (docSucessor !== '') {
                setSubstPermNameFieldError(PERMISSIONARY_NOT_FOUND);
                setPermDocumentFieldError(PERMISSIONARY_NOT_FOUND);
            } else {
                setSubstPermNameFieldError(REQUIRED_FIELD);
                setPermDocumentFieldError(REQUIRED_FIELD);
            }
        }
        validateFormikForm(formikBag, transferValidationSchema).then((res) => {
            if (Object.keys(res).length > 0) {
                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
            } else {
                formikBag.handleSubmit();
            }
        });
    };

    const handleSubmitForm = async ({ values, formikHelpers }) => {
        if (permDocumentFieldError) return;
        if (atualPermNameFieldError) return;
        if (substPermNameFieldError) return;

        try {
            setLoading(true);
            const valuesCopy = _.cloneDeep(values);
            valuesCopy.cdLinha = selectedLine?.cdLinha;

            valuesCopy.nuProcesso = requestProcessValues.nuProcesso;
            valuesCopy.nuSolicitacao = requestProcessValues.nuSolicitacaoFmt;
            valuesCopy.cdProcesso = requestProcessValues.cdProcesso;
            valuesCopy.cdSolicitacao = requestProcessValues.cdSolicitacao;

            if (
                requestProcessValues.nuProcesso &&
                requestProcessValues.nuProcesso !== '' &&
                requestProcessValues.processError
            ) {
                notification('O número do processo não existe no CPA', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
                if (formikHelpers) {
                    formikHelpers.setFieldError('nuProcesso', 'O número do processo não existe no CPA');
                }
            }

            valuesCopy.cdLinhaPermissionario = selectedActualPermissionary?.cdLinhaPermissionario;
            valuesCopy.cdEmpresa = selectedSubstitutePermissionary?.cdEmpresa;

            valuesCopy.deNomePermissionarioAntigo = selectedActualPermissionary.deNomePermissionario;
            valuesCopy.deCpfCnpjAntigo = selectedActualPermissionary.deCpfCnpj;
            valuesCopy.deNomePermissionarioNovo = selectedSubstitutePermissionary.deNomePermissionario;
            valuesCopy.deCpfCnpjNovo = selectedSubstitutePermissionary.deCpfCnpj;

            const savedTransfer = await lineEndpoint.saveTransfer(valuesCopy);

            if (savedTransfer.status === 200) {
                const transfParams = { page: 0, size: 10, request: { cdLinha } };
                await dispatch(fetchLineTransfers(transfParams));
                notification('Transferência salva com sucesso', {
                    toastId: 'SUCCESSFUL_SAVE_REQUEST',
                    type: 'success',
                });
                formikHelpers.resetForm();
                setDocAtual('');
                setDocSucessor('');
                reset();
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        }
        setLoading(false);
    };

    const callBackRequest = async (request) => {
        if (request && request.jsonSolicitacao) {
            let jsonSolicitacao;

            try {
                jsonSolicitacao = JSON.parse(request.jsonSolicitacao);
            } catch (e) {
                console.error('Erro ao processar solicitação', e);
                setRequestProcessValues({})
                return;
            }

            if (!jsonSolicitacao.cdLinha || jsonSolicitacao.cdLinha !== selectedLine.cdLinha) {
                notification('Essa solicitação não está relacionada com esta linha', {
                    toastId: 'ERROR_REQUEST',
                    type: 'error',
                });
                setRequestProcessValues({});
                return;
            }
            if (jsonSolicitacao.deCpfCnpj && jsonSolicitacao.deCpfCnpj !== '') {
                const actualPermissionary = await getPermissionaryDataByDocument(jsonSolicitacao.deCpfCnpj);
                if (actualPermissionary) {
                    setSelectedActualPermissionary(actualPermissionary);
                    setDocAtual(actualPermissionary.deCpfCnpj);
                    setAtualPermNameFieldError(null);
                } else {
                    setSelectedActualPermissionary({});
                    setAtualPermNameFieldError(PERMISSIONARY_NOT_FOUND);
                }
            }
            if (jsonSolicitacao.deCpfCnpjSucessor && jsonSolicitacao.deCpfCnpjSucessor !== '') {
                const substitute = await getPermissionaryDataByDocument(jsonSolicitacao.deCpfCnpjSucessor, true);
                if (substitute) {
                    setSelectedSubstitutePermissionary({
                        cdEmpresa: substitute.cdEmpresa,
                        deNomePermissionario:
                            substitute.deNomePermissionario || substitute.deRazaoSocial || substitute.nmCompleto,
                        deCpfCnpj: substitute.deCpfCnpj || substitute.deCnpjMatriz || substitute.deCpf,
                    });
                    setDocSucessor(substitute.deCpfCnpj || substitute.deCnpjMatriz || substitute.deCpf);
                    setSubstPermNameFieldError(null);
                } else {
                    setSelectedSubstitutePermissionary({
                        deCpfCnpj: jsonSolicitacao.deCpfCnpjSucessor,
                        deNomePermissionario: jsonSolicitacao.nmCompletoSucessor,
                    });
                    setDocSucessor(jsonSolicitacao.deCpfCnpjSucessor);
                    setPermDocumentFieldError(PERMISSIONARY_NOT_FOUND);
                    setSubstPermNameFieldError(PERMISSIONARY_NOT_FOUND);
                }
            }
        }
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={formInitials}
                initialStatus={{ holding: false }}
                validationSchema={transferValidationSchema}
                validateOnBlur={false}
                validateOnChange={true}
                onSubmit={(values, formikHelpers) => handleSubmitForm({ values, formikHelpers })}>
                {(formProps) => {
                    return (
                        <PageWrapper title="Cadastro - Histórico de transferência">
                            {loading && <Loading />}
                            <Grid container spacing={3}>
                                <Grid item container spacing={3} direction={'column'}>
                                    <InfoLineCompany selectedLine={selectedLine} />
                                </Grid>

                                <RequestProcess
                                    {...props}
                                    {...formProps}
                                    initialState={formInitials}
                                    cdLinha={cdLinha}
                                    servicesFilter={servicesFilter}
                                    requestProcessValues={requestProcessValues}
                                    setRequestProcessValues={setRequestProcessValues}
                                    callBackRequest={(request) => callBackRequest(request)}
                                    nuProcessRequired={false}
                                    fieldWidth={4}
                                />

                                <Grid xs={4}> </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Field
                                        component={CustomTextField}
                                        name="nuTermoPermissao"
                                        label="Nº Termo de permissão*"
                                        inputProps={{
                                            maxLength: 10,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Field
                                        component={CustomTextField}
                                        name="nuTermoTransferencia"
                                        label="Nº Termo de transferencia*"
                                        inputProps={{
                                            maxLength: 10,
                                        }}
                                    />
                                </Grid>

                                <Grid xs={4}> </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Field
                                        component={CustomTextField}
                                        name="docAnterior"
                                        label="CPF / CNPJ Atual*"
                                        value={docAtual}
                                        onBlur={(e) =>
                                            handleSelectPermissionaryByDocument(e.target.value, 'docAnterior')
                                        }
                                        onChange={(e) => {
                                            formProps.handleChange(e);
                                            setDocAtual(e.target.value);
                                        }}
                                        inputProps={{
                                            maxLength: 14,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        component={CustomFindField}
                                        name="nomeAnterior"
                                        label="Permissionário atual*"
                                        value={selectedActualPermissionary?.deNomePermissionario || ''}
                                        onClickButton={() => openDrawerWithContext(VIEW_CTX.searchActualPermissionary)}
                                        error={
                                            Boolean(atualPermNameFieldError) ||
                                            (Boolean(formProps.errors['nomeAnterior']) &&
                                                formProps.touched['nomeAnterior'])
                                        }
                                        helperText={atualPermNameFieldError || formProps.errors['nomeAnterior']}
                                        inputProps={{
                                            maxLength: 150,
                                        }}
                                    />
                                </Grid>

                                <Grid xs={4}> </Grid>

                                <Grid item xs={12} sm={3}>
                                    <Field
                                        component={CustomTextField}
                                        name="CpfCnpjSucessor"
                                        label="CPF / CNPJ Sucessor*"
                                        value={docSucessor}
                                        onChange={(e) => {
                                            formProps.handleChange(e);
                                            setDocSucessor(e.target.value);
                                        }}
                                        onBlur={(e) =>
                                            handleSelectPermissionaryByDocument(e.target.value, 'CpfCnpjSucessor')
                                        }
                                        error={Boolean(permDocumentFieldError)}
                                        helperText={permDocumentFieldError}
                                        inputProps={{
                                            maxLength: 14,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        component={CustomFindField}
                                        name="nomeSucessor"
                                        label="Nome/Razão Social Sucessora*"
                                        value={selectedSubstitutePermissionary?.deNomePermissionario || ''}
                                        onClickButton={() =>
                                            openDrawerWithContext(VIEW_CTX.searchSubstitutePermissionary)
                                        }
                                        error={Boolean(substPermNameFieldError)}
                                        helperText={substPermNameFieldError}
                                        inputProps={{
                                            maxLength: 150,
                                        }}
                                    />
                                </Grid>

                                <Grid xs={4}> </Grid>

                                <Grid item xs={12} sm={4}>
                                    <FastField
                                        component={CustomDateField}
                                        name="dtPublicacao"
                                        label="Data da publicação*"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FastField
                                        component={CustomDateField}
                                        name="dtVigencia"
                                        label="Data início da vigência*"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FastField
                                        component={CustomDateField}
                                        name="dtTransferencia"
                                        label="Data de transferência"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <FastField
                                        component={CustomTextField}
                                        name="deJustificativa"
                                        label="Resumo da Justificativa"
                                        multiline
                                        minRows={4}
                                        inputProps={{
                                            maxLength: 5000,
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <DefaultTable
                                        loading={loadingTable}
                                        title="Histórico de transferência"
                                        columns={transferColumns}
                                        rows={rows}
                                        paginationProps={{
                                            rowsPerPageOptions: [5, 10, 25],
                                            count: lineTransfers?.totalElements || 0,
                                            rowsPerPage: pageSize,
                                            page: page,
                                            onPageChange: handleChangePage,
                                            onRowsPerPageChange: handleChangePageSize,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        removeSpacing
                                        firstButtonLabel={'VOLTAR'}
                                        middleButtonLabel={formProps.status.holding ? 'Aguarde...' : 'SALVAR'}
                                        middleButtonDisabled={formProps.status.holding}
                                        onCancel={handleCancelTransfer}
                                        onSave={() => validateBeforeSave(formProps)}
                                    />
                                </Grid>
                            </Grid>
                        </PageWrapper>
                    );
                }}
            </Formik>
            <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
                {selectedViewContext === VIEW_CTX.searchActualPermissionary && (
                    <FindPermissionary
                        onSave={handleSelectPermissionary}
                        onClose={handleCloseDrawer}
                        requestParams={{ boPermissionario: true, cdLinha: cdLinha }}
                    />
                )}
                {selectedViewContext === VIEW_CTX.searchSubstitutePermissionary && (
                    <FindPermissionary
                        onSave={handleSelectPermissionary}
                        onClose={handleCloseDrawer}
                        isCompany={true}
                        requestParams={{ onlyCompanies: true, boAtivo: true, tipoComplementar: true }}
                    />
                )}
            </Drawer>
            <SimpleAlert open={openAlert} onClose={() => setOpenAlert(false)}>
                O permissionário selecionado já foi incluído no pedido de transferência, favor verificar.
            </SimpleAlert>
        </>
    );
};

export default TransferSlic;
